/**
 * Single Source of Truth: Production Projects & Architectural Blueprints
 * Reflects genuine production systems, proprietary IP, and public open source tooling.
 */
export const projectCategories = [
    { id: 'all', label: 'All Systems' },
    { id: 'enterprise', label: 'Enterprise & Cloud' },
    { id: 'govtech', label: 'GovTech & Mobile' },
];

export const projectsData = [
    {
        id: 'grihseva-ecosystem',
        file: 'grihseva-ecosystem.ts',
        title: 'Grihseva: Home Services Platform & Admin Control Hub',
        category: 'Dual Portals & Cloud Architecture',
        filterTag: 'enterprise',
        tier: 'flagship',
        featured: true,
        role: 'Founding Technical Architect (Pro-Bono Advisory)',
        description: 'Engineered dual customer and administrative portals utilizing Next.js React Server Components for sub-second data streaming. Designed automated service provider dispatching workflows and transactional booking state machines.',
        metrics: 'Sub-1s First Contentful Paint • Automated Provider Dispatch • Docker on AWS EC2 • Nginx Edge Cache',
        architecture: {
            concurrency: 'Next.js App Router with React Server Components (RSC) and streaming SSR',
            throughput: 'Nginx reverse proxy with SSL termination and optimized static asset edge caching',
            dataTier: 'MongoDB Atlas with optimized schema indexing for provider dispatching and transactional state validation',
            security: 'Role-based access control (RBAC), JWT rotation, and S3-signed media upload pipelines'
        },
        techStack: [
            { name: 'Next.js', type: 'frontend' },
            { name: 'React', type: 'frontend' },
            { name: 'TypeScript', type: 'language' },
            { name: 'Express', type: 'backend' },
            { name: 'MongoDB', type: 'database' },
            { name: 'AWS Cloud', type: 'cloud' },
        ],
        demoLink: 'https://grihseva.com',
        isLivePublic: true,
        demoLabel: 'Live Platform',
        isRepoPrivate: true,
        codeStatus: 'Private Client IP',
    },
    {
        id: 'phnk-networks',
        file: 'phnk-network-engine.ts',
        title: 'Phnk Networks: AI Social & Media Platform',
        category: 'High-Concurrency & Media Streaming',
        filterTag: 'enterprise',
        tier: 'flagship',
        featured: true,
        role: 'Founding Lead Full-Stack Architect',
        description: 'High-concurrency creator ecosystem handling low-latency live interactions, asynchronous diffusion rendering queues, and digital wallet settlements across distributed cloud nodes.',
        metrics: '100+ DB Tables • 260+ Endpoints • Sub-200ms Kafka Chat • 70% AWS Compression',
        architecture: {
            concurrency: 'WebSockets & Socket.IO event-loop cluster + Kafka streaming supporting 500+ concurrent users with < 200ms latency',
            throughput: 'Asynchronous worker queues on AWS (EC2, Lambda) with automated image/video compression reducing payloads by 70%',
            dataTier: '100+ relational MySQL & DynamoDB tables with 20% query latency optimization + MongoDB document feeds',
            security: 'Razorpay payment gateway webhook validation with HMAC-SHA256 signatures, JWT rotation, and AWS Certificate Manager HTTPS'
        },
        techStack: [
            { name: 'Node.js', type: 'backend' },
            { name: 'React', type: 'frontend' },
            { name: 'AWS Cloud', type: 'cloud' },
            { name: 'MySQL', type: 'database' },
            { name: 'MongoDB', type: 'database' },
            { name: 'WebSockets', type: 'realtime' },
        ],
        demoLink: 'https://phnk.in/',
        isLivePublic: true,
        demoLabel: 'Live Platform',
        isRepoPrivate: true,
        codeStatus: 'Private Enterprise IP',
    },
    {
        id: 'amritpex-2023',
        file: 'amritpex-national.ts',
        title: 'AMRITPEX 2023: National Digital Infrastructure',
        category: 'National GovTech & Mobile Stores',
        filterTag: 'govtech',
        tier: 'enterprise',
        featured: false,
        role: 'Technical Lead & Backend Architect',
        description: 'High-throughput ticketing and entry verification infrastructure deployed for the Department of Posts (GoI) at Pragati Maidan. Processed high-volume physical gate check-ins with cryptographically signed digital passes.',
        metrics: '10,000+ Daily Active Users • Zero Downtime SLA • Sub-300ms Pass Generation • Dual Mobile Store Launch',
        architecture: {
            concurrency: 'PM2 process clustering across multi-core Node.js instances with automated health checks',
            throughput: 'Sub-300ms pass lookup and verification handling 10,000+ daily active visitors during peak physical exhibition surges',
            dataTier: 'Indexed relational attendee registry and QR/OTP validation optimized for high-read physical gate verification',
            security: 'Tamper-evident cryptographically signed entry tokens, OTP verification, and native camera scanning bridges'
        },
        techStack: [
            { name: 'Node.js', type: 'backend' },
            { name: 'React', type: 'frontend' },
            { name: 'Express', type: 'backend' },
            { name: 'Google Play', type: 'mobile' },
            { name: 'App Store', type: 'mobile' },
            { name: 'PM2', type: 'devops' },
        ],
        isLivePublic: false,
        platformStatus: 'National Event Concluded (Feb 2023 – Mar 2023)',
        isRepoPrivate: true,
        codeStatus: 'GovTech Restricted Code',
    },
    {
        id: 'kalpkriti-analytics',
        file: 'kalpkriti-analytics.ts',
        title: 'Kalpkriti: Workflow Automation & Analytics Engine',
        category: 'BigQuery & Simulation Engines',
        filterTag: 'enterprise',
        tier: 'enterprise',
        featured: false,
        role: 'Full-Stack Software Engineer & Data Analyst',
        description: 'Distributed data computation pipeline powering automated 2D physics simulations and asset ingestion workflows for the Tweencraft animation engine, backed by real-time behavioral event tracking.',
        metrics: '5,000+ Req/Min • 1M+ BigQuery Events • 150+ Production Endpoints • 90% Manual Error Reduction',
        architecture: {
            concurrency: 'Asynchronous batch workers and Python automated simulation execution runners reducing manual errors by 90%',
            throughput: 'Scaled 150+ Node.js/Express.js APIs handling 5,000+ requests per minute with a 20% overall performance improvement',
            dataTier: 'Google BigQuery analytical warehouse analyzing 1M+ user interactions + MongoDB schema indexing + Redis cache',
            security: 'Role-based administrative authentication with automated error telemetry and logging'
        },
        techStack: [
            { name: 'Python', type: 'language' },
            { name: 'Node.js', type: 'backend' },
            { name: 'React', type: 'frontend' },
            { name: 'Google BigQuery', type: 'cloud' },
            { name: 'MongoDB', type: 'database' },
            { name: 'Express', type: 'backend' },
        ],
        demoLink: 'https://tweencraft.in/',
        isLivePublic: true,
        demoLabel: 'Live Platform',
        isRepoPrivate: true,
        codeStatus: 'Internal Enterprise IP',
    },
];

export default projectsData;
